import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowLeft, RefreshCw, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import ContentEditor from "@/components/admin/ContentEditor";
import InvitesManager from "@/components/admin/InvitesManager";

function Login() {
  const { login } = useAuth();
  const wedding = useContent();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 grain relative">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm"
      >
        <Link to="/" className="overline text-white/40 hover:text-white transition-colors inline-flex items-center gap-2 mb-12">
          <ArrowLeft size={14} /> Volver
        </Link>
        <p className="overline text-white/50">{wedding.monogram}</p>
        <h1 className="font-display text-4xl mt-4 mb-10">Panel de Administración</h1>
        <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
          <input
            className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-white transition-colors"
            placeholder="Correo" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email"
          />
          <input
            className="w-full bg-transparent border-0 border-b border-white/20 rounded-none py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-white transition-colors"
            placeholder="Contraseña" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
          />
          <button
            type="submit" disabled={loading}
            className="w-full bg-white text-ink py-4 border border-white hover:bg-transparent hover:text-white transition-colors duration-300 overline disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? "Entrando…" : "Iniciar Sesión"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function ExpandableText({ text, slug, short = 90 }) {
  const [open, setOpen] = useState(false);
  if (!text) return <>—</>;
  if (text.length <= short) return <span className="break-words">{text}</span>;
  return (
    <span className="break-words">
      {open ? text : `${text.slice(0, short).trimEnd()}…`}{" "}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="underline underline-offset-2 decoration-black/30 text-ink hover:decoration-ink transition-colors"
        data-testid={`read-more-${slug}`}
      >
        {open ? "leer menos" : "leer más…"}
      </button>
    </span>
  );
}

function Dashboard() {
  const { admin, logout } = useAuth();
  const wedding = useContent();
  const [tab, setTab] = useState("rsvp");
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/invites"), api.get("/rsvp/stats")]);
      setInvites(r.data);
      setStats(s.data);
    } catch {
      toast.error("No se pudieron cargar los datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const statCards = stats
    ? [
        { l: "Invitaciones", v: stats.total_invites },
        { l: "Personas invitadas", v: stats.total_passes },
        { l: "Confirman", v: stats.attending },
        { l: "No asisten", v: stats.declined },
        { l: "Pendientes", v: stats.pending },
        { l: "Personas confirmadas", v: stats.total_guests },
      ]
    : [];

  const sortedInvites = [...invites].sort((a, b) => a.name.localeCompare(b.name, "es"));

  const toggleSent = async (inv) => {
    const next = !inv.sent;
    setInvites((list) => list.map((i) => (i.id === inv.id ? { ...i, sent: next } : i)));
    try {
      await api.put(`/invites/${inv.id}`, { sent: next });
    } catch {
      setInvites((list) => list.map((i) => (i.id === inv.id ? { ...i, sent: !next } : i)));
      toast.error("No se pudo actualizar");
    }
  };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/invitacion/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace de invitación copiado");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success("Enlace de invitación copiado");
      else toast.error(`No se pudo copiar. Enlace: ${url}`);
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="border-b border-black/10 sticky top-0 bg-paper/90 backdrop-blur z-10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="overline text-[color:var(--muted)]">{wedding.monogram} — Administración</p>
            <p className="font-display text-xl">RSVP</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="w-11 h-11 border border-black/15 flex items-center justify-center hover:bg-ink hover:text-white transition-colors" data-testid="admin-refresh" aria-label="Recargar">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={doLogout} className="inline-flex items-center gap-2 border border-black/15 px-5 h-11 hover:bg-ink hover:text-white transition-colors overline" data-testid="admin-logout">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
        <div className="flex gap-5 sm:gap-8 mb-10 border-b border-black/10 overflow-x-auto">
          {[{ k: "rsvp", l: "Confirmaciones" }, { k: "invites", l: "Invitados" }, { k: "content", l: "Contenido" }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`overline whitespace-nowrap pb-4 -mb-px border-b-2 transition-colors ${tab === t.k ? "border-ink text-ink" : "border-transparent text-[color:var(--muted)] hover:text-ink"}`}
              data-testid={`tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "rsvp" && (<>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10 border border-black/10 mb-12">
          {statCards.map((c) => (
            <div key={c.l} className="bg-paper p-5 sm:p-8">
              <p className="font-display text-4xl sm:text-5xl">{c.v}</p>
              <p className="overline text-[color:var(--muted)] mt-2 sm:mt-3">{c.l}</p>
            </div>
          ))}
        </div>

        <div className="hidden lg:block overflow-x-auto border border-black/10">
          <table className="w-full text-left" data-testid="admin-rsvp-table">
            <thead>
              <tr className="border-b border-black/10">
                {["Enviada", "Nombre", "Estado", "Pases", "Asisten", "Canción", "Mensaje", "Respondió", "Enlace"].map((h) => (
                  <th key={h} className="overline text-[color:var(--muted)] font-medium px-5 py-4 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedInvites.length === 0 && !loading && (
                <tr><td colSpan={9} className="px-5 py-10 text-center font-light text-[color:var(--muted)]">Aún no hay invitados.</td></tr>
              )}
              {sortedInvites.map((inv) => (
                <tr key={inv.id} className="border-b border-black/5 hover:bg-secondary/60 transition-colors align-top">
                  <td className="px-5 py-4">
                    <button
                      onClick={() => toggleSent(inv)}
                      className={`w-6 h-6 border flex items-center justify-center transition-colors ${inv.sent ? "bg-ink border-ink text-white" : "border-black/25 text-transparent hover:border-ink"}`}
                      data-testid={`sent-toggle-${inv.slug}`}
                      aria-label={`Marcar enviada ${inv.name}`}
                      title={inv.sent ? "Invitación enviada" : "Marcar como enviada"}
                    >
                      <Check size={14} strokeWidth={2.5} />
                    </button>
                  </td>
                  <td className="px-5 py-4 font-display text-lg whitespace-nowrap">{inv.name}</td>
                  <td className="px-5 py-4">
                    {inv.rsvp ? (
                      <span className={`inline-block text-xs px-3 py-1 ${inv.rsvp.attending ? "bg-ink text-white" : "border border-black/20 text-[color:var(--muted)]"}`}>
                        {inv.rsvp.attending ? "Sí asiste" : "No asiste"}
                      </span>
                    ) : (
                      <span className="inline-block text-xs px-3 py-1 border border-dashed border-black/25 text-[color:var(--muted)]">Pendiente</span>
                    )}
                  </td>
                  <td className="px-5 py-4 tabular-nums">{inv.max_guests}</td>
                  <td className="px-5 py-4 tabular-nums">{inv.rsvp?.attending ? inv.rsvp.guests : "—"}</td>
                  <td className="px-5 py-4 text-sm font-light max-w-[160px]">{inv.rsvp?.song || "—"}</td>
                  <td className="px-5 py-4 text-sm font-light max-w-[240px]"><ExpandableText text={inv.rsvp?.message} slug={inv.slug} /></td>
                  <td className="px-5 py-4 text-sm font-light whitespace-nowrap text-[color:var(--muted)]">
                    {inv.rsvp ? new Date(inv.rsvp.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" }) : "—"}
                  </td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => copyLink(inv.slug)}
                      className="w-9 h-9 border border-black/15 flex items-center justify-center hover:bg-ink hover:text-white transition-colors"
                      data-testid={`copy-link-${inv.slug}`}
                      aria-label={`Copiar enlace de ${inv.name}`}
                      title="Copiar enlace de invitación"
                    >
                      <Copy size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="lg:hidden border border-black/10 divide-y divide-black/5" data-testid="admin-rsvp-list-mobile">
          {sortedInvites.length === 0 && !loading && (
            <p className="px-4 py-8 text-center font-light text-[color:var(--muted)]">Aún no hay invitados.</p>
          )}
          {sortedInvites.map((inv) => (
            <div key={inv.id} className="px-4 py-3" data-testid={`rsvp-row-mobile-${inv.slug}`}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleSent(inv)}
                  className={`w-6 h-6 shrink-0 border flex items-center justify-center transition-colors ${inv.sent ? "bg-ink border-ink text-white" : "border-black/25 text-transparent"}`}
                  data-testid={`sent-toggle-mobile-${inv.slug}`}
                  aria-label={`Marcar enviada ${inv.name}`}
                >
                  <Check size={14} strokeWidth={2.5} />
                </button>
                <p className="font-display text-base flex-1 truncate">{inv.name}</p>
                {inv.rsvp ? (
                  <span className={`text-[10px] px-2 py-0.5 shrink-0 ${inv.rsvp.attending ? "bg-ink text-white" : "border border-black/20 text-[color:var(--muted)]"}`}>
                    {inv.rsvp.attending ? "Sí asiste" : "No asiste"}
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 shrink-0 border border-dashed border-black/25 text-[color:var(--muted)]">Pendiente</span>
                )}
                <button
                  onClick={() => copyLink(inv.slug)}
                  className="w-8 h-8 shrink-0 border border-black/15 flex items-center justify-center active:bg-ink active:text-white transition-colors"
                  data-testid={`copy-link-mobile-${inv.slug}`}
                  aria-label={`Copiar enlace de ${inv.name}`}
                >
                  <Copy size={13} />
                </button>
              </div>
              <div className="mt-1.5 ml-9 flex flex-wrap gap-x-3 text-xs font-light text-[color:var(--muted)]">
                <span>{inv.max_guests} {inv.max_guests > 1 ? "pases" : "pase"}{inv.rsvp?.attending ? ` · asisten ${inv.rsvp.guests}` : ""}</span>
                {inv.rsvp && <span>{new Date(inv.rsvp.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}</span>}
                {inv.rsvp?.song && <span className="truncate max-w-full">Canción: {inv.rsvp.song}</span>}
              </div>
              {inv.rsvp?.message && (
                <p className="mt-1 ml-9 text-xs font-light text-[color:var(--muted)] break-words">
                  <ExpandableText text={inv.rsvp.message} slug={`mobile-${inv.slug}`} short={60} />
                </p>
              )}
            </div>
          ))}
        </div>
        </>)}

        {tab === "content" && <ContentEditor />}
        {tab === "invites" && <InvitesManager />}
      </main>
    </div>
  );
}

export default function Admin() {
  const { admin, ready } = useAuth();
  useEffect(() => {
    document.title = "Administración — Boda de Caro y Roberto";
  }, []);
  if (!ready) {
    return <div className="min-h-screen bg-ink flex items-center justify-center text-white/50 overline">Cargando…</div>;
  }
  return admin ? <Dashboard /> : <Login />;
}
